import { useMutation } from '@tanstack/react-query';
import type {
  TExportSummaryOptions,
  TSummaryExportValue,
  TSummaryRequestOptions,
} from './useSummaryExport.types.ts';
import { useApi } from '#providers/api/index.ts';
import { useExperience } from '#providers/experiences/use-experience/index.ts';
import { useProjects } from '#providers/projects/index.ts';

export const useSummaryExport = (): TSummaryExportValue => {
  const api = useApi();
  const { experienceId, experience } = useExperience();
  const { isPrimaryUser, requireProjectId } = useProjects();

  const requestSummary = (options: TSummaryRequestOptions) => {
    const projectId = requireProjectId();

    return api.geV2.exportExperienceSummaryV2({
      project_id: projectId,
      experience_id: experienceId,
      ...options,
    });
  };

  const exportMutation = useMutation({
    mutationFn: (options?: TExportSummaryOptions) =>
      requestSummary({ summary_id: options?.summaryId }),
  });

  const emailMutation = useMutation({
    mutationFn: () => requestSummary({ send_email: true }),
  });

  const canExportSummary =
    Boolean(experience?.summary_export_enabled) && isPrimaryUser;

  return {
    canExportSummary,
    summary: exportMutation.data ?? emailMutation.data,
    isExportingSummary: exportMutation.isPending,
    exportSummaryError: exportMutation.error ?? undefined,
    isEmailingSummary: emailMutation.isPending,
    emailSummaryError: emailMutation.error ?? undefined,
    hasEmailedSummary: Boolean(emailMutation.data?.emailed),
    exportSummary: exportMutation.mutateAsync,
    emailSummary: emailMutation.mutateAsync,
  };
};
